import { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import {
  convertRfcTimeToLocalDateTimeStartOfDay,
  getTodayLocalDateTimeStartOfDay,
} from '../../../../utils/dateAndTime/dateAndTime';
import {
  FirstDayOfWeek,
  getFirstDayOfTheWeek,
} from '../../../../utils/dateAndTime/weekStart';
import {
  isDailyAgendaWeeklyPickerLayout,
  isDailyTimeSlotsWeeklyPickerLayout,
} from '../../../../utils/layouts';
import { CalendarState } from '../../controller';
import { MemoizedViewModalFactory } from '../viewModel';

export enum DateAvailabilityStatus {
  HAS_AVAILABLE_SLOTS = 'has_available_slots',
  HAS_UNAVAILABLE_SLOTS = 'has_slots',
}

export type DatePickerViewModel = {
  selectedDate: string;
  dateAvailabilityStatuses: Map<string, DateAvailabilityStatus>;
  firstDayOfTheWeek: FirstDayOfWeek;
  locale: string;
  todayLocalDateTime: string;
  accessibility: {
    nextAriaLabel: string;
    prevAriaLabel: string;
  };
  pricingPlanExpirationDate?: string;
};

export const memoizedDatePickerViewModel: MemoizedViewModalFactory<DatePickerViewModel> =
  {
    dependencies: {
      settings: ['calendarLayout'],
      state: ['selectedDate', 'availableSlotsPerDay', 'selectedTimezone', 'pricingPlanExpirationDate'],
    },
    createViewModel: createDatePickerViewModel,
  };

export function createDatePickerViewModel({
  state,
  context: { languageSettings, t, settings, settingsParams },
}: ViewModelFactoryParams<
  CalendarState,
  CalendarContext
>): DatePickerViewModel {
  const { selectedDate, availableSlotsPerDay, selectedTimezone, pricingPlanExpirationDate } = state;

  const firstDayOfTheWeek = getFirstDayOfTheWeek(
    languageSettings!.dateRegionalSettingsLocale!,
  );
  const todayLocalDateTime = getTodayLocalDateTimeStartOfDay(selectedTimezone!);

  const dateAvailabilityStatuses = new Map<string, DateAvailabilityStatus>();

  availableSlotsPerDay?.timeSlots?.forEach((slotAvailability: TimeSlot) => {
    const localDateTime = convertRfcTimeToLocalDateTimeStartOfDay(
      slotAvailability?.localStartDate!,
    );
    dateAvailabilityStatuses.set(
      localDateTime,
      slotAvailability.bookable
        ? DateAvailabilityStatus.HAS_AVAILABLE_SLOTS
        : DateAvailabilityStatus.HAS_UNAVAILABLE_SLOTS,
    );
  });

  const isWeeklyDatePicker: boolean =
    isDailyTimeSlotsWeeklyPickerLayout(settings, settingsParams) ||
    isDailyAgendaWeeklyPickerLayout(settings, settingsParams);
  const accessibility = {
    nextAriaLabel: isWeeklyDatePicker
      ? t('app.week-picker.accessibility.next-week')
      : t('app.date-picker.accessibility.next-month'),
    prevAriaLabel: isWeeklyDatePicker
      ? t('app.week-picker.accessibility.prev-week')
      : t('app.date-picker.accessibility.prev-month'),
  };

  return {
    selectedDate: selectedDate!,
    dateAvailabilityStatuses,
    firstDayOfTheWeek,
    todayLocalDateTime,
    locale: languageSettings.language!,
    accessibility,
    pricingPlanExpirationDate,
  };
}
